import { useEffect, useMemo, useState } from 'react';

/**
 * Zone → bay location picker, used wherever a form asks for a warehouse
 * location. One flat list of every bay ("Rack 1 Shelf B…" × hundreds) was
 * unusable; this asks for the zone first and then only that zone's bays.
 * A zone with a single bay (Bottling Room, Still House, Duty Paid Area…)
 * selects it straight away, so there's no second dropdown.
 *
 * Drop-in for a <select>: value is the location id, and onChange is called
 * with an event-like object ({ target: { value } }) so existing handlers
 * written for e.target.value keep working.
 */
const baseStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
  background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};

const NO_ZONE = '__none__';

export default function LocationSelect({ locations = [], value = '', onChange, placeholder = '— select location —', required = false, disabled = false, style }) {
  const zones = useMemo(() => {
    const map = new Map();
    for (const l of locations) {
      const id = l.zone_id || NO_ZONE;
      if (!map.has(id)) map.set(id, { id, name: l.zone_name || 'Other locations', facility: l.facility_name || '', bays: [] });
      map.get(id).bays.push(l);
    }
    const list = [...map.values()];
    for (const z of list) z.bays.sort((a, b) => String(a.name ?? a.code).localeCompare(String(b.name ?? b.code), undefined, { numeric: true }));
    list.sort((a, b) => (a.facility || '').localeCompare(b.facility || '') || a.name.localeCompare(b.name));
    return list;
  }, [locations]);

  const zoneOf = (locId) => {
    const l = locations.find((x) => x.id === locId);
    return l ? (l.zone_id || NO_ZONE) : '';
  };

  const [zoneId, setZoneId] = useState(() => zoneOf(value));
  // Follow the value when it's set from outside (e.g. "set every row's location").
  useEffect(() => { if (value) { const z = zoneOf(value); if (z && z !== zoneId) setZoneId(z); } }, [value, locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (id) => onChange?.({ target: { value: id } });
  const zone = zones.find((z) => z.id === zoneId);
  const facilities = new Set(zones.map((z) => z.facility).filter(Boolean));

  function pickZone(id) {
    setZoneId(id);
    const z = zones.find((x) => x.id === id);
    emit(z && z.bays.length === 1 ? z.bays[0].id : '');
  }

  const label = (z) => (facilities.size > 1 && z.facility ? `${z.name} (${z.facility})` : z.name);

  return (
    <div style={{ display: 'flex', gap: 6, ...style }}>
      <select style={{ ...baseStyle, flex: 1 }} value={zoneId} disabled={disabled} required={required && !zone}
        onChange={(e) => pickZone(e.target.value)}>
        <option value="">{placeholder}</option>
        {zones.map((z) => <option key={z.id} value={z.id}>{label(z)}</option>)}
      </select>
      {zone && zone.bays.length > 1 && (
        <select style={{ ...baseStyle, flex: 1 }} value={value || ''} disabled={disabled} required={required}
          onChange={(e) => emit(e.target.value)}>
          <option value="">— pick a bay —</option>
          {zone.bays.map((l) => <option key={l.id} value={l.id}>{l.name ?? l.code}</option>)}
        </select>
      )}
    </div>
  );
}
